import BaseScene from '@scenes/base/BaseScene'
import SecureRuffleBridge from './SecureRuffleBridge'
import './FlashBridgeHelper'

const DEFAULT_FLASH_BASE = 'assets/media/flash/'
const ABSOLUTE_URL_REGEX = /^https?:\/\//i
const BOOT_SWF = 'boot.swf'
const BOOT_AS3_SWF = 'boot_as3.swf'
const GAMES_PATH_PREFIX = 'games/'

function ensureTrailingSlash(value) {
    if (!value) {
        return ''
    }

    return value.endsWith('/') ? value : `${value}/`
}

function resolveBasePath() {
    try {
        if (typeof BUILD_TIME_ASSETS_URL !== 'undefined' && BUILD_TIME_ASSETS_URL) {
            const base = BUILD_TIME_ASSETS_URL.replace(/\/$/, '')
            return ensureTrailingSlash(`${base}/assets/media/flash`)
        }
    } catch (error) {
        // Ignore missing BUILD_TIME_ASSETS_URL definitions
    }

    return ensureTrailingSlash(DEFAULT_FLASH_BASE)
}

function sanitizeItemId(value) {
    const candidate = Array.isArray(value) ? value[0] : value
    const parsed = parseInt(candidate, 10)

    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function clampCoins(value) {
    const parsed = parseInt(value, 10)

    if (!Number.isFinite(parsed) || parsed < 0) {
        return 0
    }

    return Math.min(parsed, 10000)
}

function normalizeMusicKey(value) {
    if (value === false || value === null || value === undefined) {
        return null
    }

    if (Array.isArray(value)) {
        for (const entry of value) {
            const normalized = normalizeMusicKey(entry)

            if (normalized) {
                return normalized
            }
        }

        return null
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) && value > 0 ? String(value) : null
    }

    if (typeof value === 'string') {
        const trimmed = value.trim()
        return trimmed.length > 0 ? trimmed : null
    }

    return null
}

function normalizeMusicList(values) {
    if (!values) {
        return []
    }

    const list = Array.isArray(values) ? values : [values]

    return list
        .map(entry => normalizeMusicKey(entry))
        .filter(Boolean)
}

export default class RuffleController extends BaseScene {

    constructor(key) {
        super(key)

        this.player = null
        this.container = null
        this.domContainer = null
        this.domPointerEnabled = false

        this.path = ''
        this.music = 0
        this.preLoop = null
        this.mainLoops = null
        this.gameName = null
        this.isQuest = false

        this.gameEvents = new EventTarget()
        this.eventListeners = []
        this.gameSurfaceVisible = false

        this.basePath = resolveBasePath()

        this.playerEventRemovers = []
        this.pointerFallbackTimer = null

        this.bridgeKeys = [
            'addEventListener',
            'addItemToMyInventory',
            'buyInventory',
            'CardJitsuDownloadedAnimation',
            'closeRoom',
            'closeWidget',
            'getCurrentServerRoomId',
            'getFrameColor',
            'getGamesPath',
            'getLocalizedFrame',
            'getMyLastRoom',
            'getMyPlayer',
            'getMyPlayerData',
            'getMyPlayerHex',
            'getMyPlayerId',
            'getMyPlayerNickname',
            'getPath',
            'getPlayerObjectById',
            'getPuffleColor',
            'isItemInMyInventory',
            'isItemOnMyPlayer',
            'isMyPlayerMember',
            'joinRoom',
            'loadCardJitsuWorld',
            'onLoadComplete',
            'onWidgetLoadComplete',
            'playerWearingItem',
            'sendDancingPacket',
            'sendEndMidwayGame',
            'sendGameEvent',
            'sendGameOver',
            'sendGameOverAltLoader',
            'sendJoinLastRoom',
            'stampEarned',
            'stampEarnedAltLoader',
            'stampIsOwnedByMe',
            'startGameMusic',
            'startMusicById',
            'stopGameMusic',
            'stopMusic'
        ]

        this.privateBridgeKeys = ['removeEventListeners']

        this.secureBridge = new SecureRuffleBridge(this)
        this._sceneReady = false

        /** Anti-cheat session token issued by the server for the current game */
        this.gameSessionToken = null
    }

    /**
     * Access the GameSession network plugin for token management.
     * @returns {import('../../engine/network/plugins/plugins/GameSession').default|null}
     */
    get gameSessionPlugin() {
        // Network plugins are bound as event listeners; the PluginManager
        // doesn't expose them by name. Instead we stash a reference on the
        // network instance when the plugin is constructed (see GameSession.js).
        // Fallback: look in the plugins cache.
        return this.network?._gameSessionPlugin ?? null
    }

    get client() {
        return this.world?.client ?? null
    }

    get clientObject() {
        return this.client?.penguin?.items?.flat ?? {}
    }

    create() {
        window.RufflePlayer = window.RufflePlayer || {}

        this.playerStyle = {
            width: '100%',
            height: '100%',
            pointerEvents: 'auto'
        }

        this.container = this.add.dom(760, 480)
        this.container.visible = false
        this.domContainer = this.sys?.domContainer ?? this.game?.domContainer ?? null

        this.setPointerEnabled(false)

        this._sceneReady = true
        this.events.emit('scene-ready')
    }

    update() {
        if (this.interface?.prompt?.isPromptVisible) {
            this.sendToBack()
        } else {
            this.resetDepth()
        }
    }

    bootGame(game, musicOverride = null) {
        if (!this._sceneReady) {
            this.events.once('scene-ready', () => this.bootGame(game, musicOverride))
            return
        }

        const config = this.normalizeGameConfig(game, musicOverride)

        if (!config) {
            console.warn('[RuffleController] Unable to normalize game configuration', game)
            return
        }

        this.path = config.path
        this.music = config.music
        this.preLoop = config.preLoop
        this.mainLoops = config.mainLoops
        this.gameName = config.gameName
        this.isQuest = config.isQuest

        // §5 Anti-Cheat: Request a session token before the game starts
        this.gameSessionToken = null
        this._requestGameSessionToken(config.gameName)

        this.startBridgeSession()

        this.events.once('update', () => this.boot(config.useAltLoader))
    }

    /**
     * Request a game session token from the server.
     * Non-blocking — the token will be available by the time the player
     * finishes playing and triggers sendGameOver.
     */
    _requestGameSessionToken(gameName) {
        if (!gameName || !this.network) return

        const gameId = String(gameName)

        // Listen for the server response (one-shot)
        const onToken = (args) => {
            if (args?.gameId === gameId && args?.token) {
                this.gameSessionToken = args.token
            }
        }
        const onError = (args) => {
            console.warn('[RuffleController] Game session token request failed:', args?.error)
        }

        this.network.events.once('game_session_started', onToken)
        this.network.events.once('game_session_error', onError)

        this.network.send('start_game_session', { gameId })
    }

    bootRoom(roomPath, mute = false) {
        if (!this._sceneReady) {
            this.events.once('scene-ready', () => this.bootRoom(roomPath, mute))
            return
        }

        if (!roomPath) {
            return
        }

        this.path = roomPath
        this.gameName = null

        this.startBridgeSession()

        this.events.once('update', () => {
            this.loadPlayer(this.resolveUrl(roomPath), this.playerStyle, {
                splashScreen: false,
                volume: mute ? 0 : 0.7
            })
        })
    }

    boot(useAltLoader = false) {
        const loaderPath = this.resolveUrl(useAltLoader ? BOOT_AS3_SWF : BOOT_SWF)
        this.loadPlayer(loaderPath, this.playerStyle)
    }

    startBridgeSession() {
        this.secureBridge.createBridge()
        this.createSecureFlashInterface()

        this.gameSurfaceVisible = false
        this.clearPointerFallback()

        if (this.container) {
            this.container.visible = false
        }

        this.setPointerEnabled(false)
    }

    loadPlayer(url, style, extraOptions = {}) {
        this.teardownAudio()
        this.removePlayer()

        this.clearPlayerEventHooks()

        const ruffle = window.RufflePlayer?.newest?.()

        if (!ruffle) {
            console.error('[RuffleController] Ruffle runtime not detected')
            return
        }

        this.player = ruffle.createPlayer()
        this.container.setElement(this.player, style)

        this.applyPointerState(this.container?.node)
        this.applyPointerState(this.player)

        const unlockPointer = () => {
            if (this.gameSurfaceVisible && this.domPointerEnabled) {
                return
            }

            this.clearPointerFallback()
            this.showGameSurface({ hideInterface: true, stopMusic: true })
        }

        const registerOnce = (eventName) => {
            if (!this.player?.addEventListener) {
                return
            }

            const handler = () => {
                unlockPointer()
            }

            this.player.addEventListener(eventName, handler, { once: true })
            this.playerEventRemovers.push(() => {
                try {
                    this.player?.removeEventListener?.(eventName, handler)
                } catch (error) {
                    console.debug('[RuffleController] Failed to remove player event listener', eventName, error)
                }
            })
        }

        registerOnce('loadedmetadata')
        registerOnce('loadeddata')
        registerOnce('load')
        registerOnce('playing')

        try {
            const loadResult = this.player.load({
                url,
                allowScriptAccess: true,
                menu: false,
                contextMenu: 'off',
                scale: 'noborder',
                autoplay: 'on',
                warnOnUnsupportedContent: false,
                splashScreen: false,
                logLevel: localStorage.logging === 'true' ? 'info' : 'error',
                ...extraOptions
            })

            if (loadResult && typeof loadResult.then === 'function') {
                loadResult
                    .then(() => unlockPointer())
                    .catch(error => {
                        console.warn('[RuffleController] Player load promise rejected', error)
                    })
            }
        } catch (error) {
            console.warn('[RuffleController] Failed to load SWF', error)
        }

        this.pointerFallbackTimer = setTimeout(() => {
            if (!this.domPointerEnabled) {
                console.warn('[RuffleController] Pointer fallback triggered; proceeding without Flash callback')
                this.showGameSurface({ hideInterface: true, stopMusic: true })
            }

            this.pointerFallbackTimer = null
        }, 2000)
    }

    teardownAudio() {
        try {
            this.soundManager?.stopAll()
        } catch (error) {
            console.warn('[RuffleController] Failed to stop Howler audio before boot', error)
        }

        this.forceStopRuffleAudio()
    }

    stop() {
        this.events.off('update')

        this.removeEventListeners()
        this.removePlayer()
        this.forceStopRuffleAudio()

        try {
            this.soundManager?.stopAll()
        } catch (error) {
            console.warn('[RuffleController] Failed to stop Howler audio on stop', error)
        }

        try {
            this.stopMusic()
        } catch (error) {
            console.warn('[RuffleController] Failed to stop tracked music', error)
        }

        this.secureBridge.destroyBridge()
        this.destroySecureFlashInterface()

        this.setPointerEnabled(false)

        this.resetDepth()

        if (this.scene?.isActive()) {
            this.scene.stop()
        }
    }

    forceStopRuffleAudio() {
        try {
            if (this.player) {
                try {
                    if (this.player.volume !== undefined) {
                        this.player.volume = 0
                    }

                    if (typeof this.player.pause === 'function') {
                        this.player.pause()
                    }

                    if (typeof this.player.setVolume === 'function') {
                        this.player.setVolume(0)
                    }
                } catch (error) {
                    console.log('[RuffleController] Failed to mute active player', error)
                }
            }

            if (typeof AudioContext !== 'undefined') {
                if (window.webkitAudioContext && window.webkitAudioContext.prototype) {
                    try {
                        const contexts = window.webkitAudioContext._contexts || []
                        contexts.forEach(ctx => {
                            if (ctx.state === 'running') {
                                ctx.suspend()
                            }
                        })
                    } catch (error) {
                        console.log('[RuffleController] Unable to suspend webkit audio contexts', error)
                    }
                }

                document.querySelectorAll('ruffle-embed, ruffle-object, ruffle-player').forEach(element => {
                    try {
                        if (element.volume !== undefined) {
                            element.volume = 0
                        }

                        if (typeof element.pause === 'function') {
                            element.pause()
                        }

                        if (element.audioContext?.state === 'running') {
                            element.audioContext.suspend()
                        }
                    } catch (error) {
                        console.log('[RuffleController] Unable to suspend element audio context', error)
                    }
                })

                try {
                    for (const key in window) {
                        const candidate = window[key]

                        if (candidate && typeof candidate.suspend === 'function' && candidate.state === 'running') {
                            candidate.suspend()
                        }
                    }
                } catch (error) {
                    console.log('[RuffleController] Unable to suspend global audio contexts', error)
                }

                try {
                    document.querySelectorAll('audio, video').forEach(element => {
                        if (!element.paused) {
                            element.pause()
                            element.volume = 0
                        }
                    })
                } catch (error) {
                    console.log('[RuffleController] Unable to pause DOM media elements', error)
                }
            }
        } catch (error) {
            console.warn('[RuffleController] Error while forcing Ruffle audio shutdown', error)
        }
    }

    removePlayer() {
        this.clearPlayerEventHooks()

        if (this.player) {
            try {
                if (typeof this.player.stop === 'function') {
                    this.player.stop()
                }

                if (this.player.volume !== undefined) {
                    this.player.volume = 0
                }

                if (typeof this.player.pause === 'function') {
                    this.player.pause()
                }

                this.forceStopRuffleAudio()

                const playerRef = this.player
                setTimeout(() => {
                    try {
                        if (playerRef && typeof playerRef.remove === 'function') {
                            playerRef.remove()
                        }
                    } catch (error) {
                        console.warn('[RuffleController] Failed to remove Ruffle player element', error)
                    }
                }, 100)
            } catch (error) {
                console.warn('[RuffleController] Error removing Ruffle player', error)
                this.forceStopRuffleAudio()
            }
        }

        this.player = null
        this.gameSurfaceVisible = false

        if (this.container) {
            this.container.visible = false
        }
    }

    clearPointerFallback() {
        if (this.pointerFallbackTimer) {
            clearTimeout(this.pointerFallbackTimer)
            this.pointerFallbackTimer = null
        }
    }

    clearPlayerEventHooks() {
        this.clearPointerFallback()

        if (Array.isArray(this.playerEventRemovers) && this.playerEventRemovers.length > 0) {
            for (const remover of this.playerEventRemovers) {
                try {
                    remover()
                } catch (error) {
                    console.debug('[RuffleController] Error while clearing player listener', error)
                }
            }
        }

        this.playerEventRemovers = []
    }

    getBridgeKeys() {
        return [...this.bridgeKeys, ...this.privateBridgeKeys]
    }

    getBridgeApi() {
        return {
            addEventListener: (...args) => this.addEventListener(...args),
            removeEventListeners: () => this.removeEventListeners(),
            addItemToMyInventory: (item) => this.addItemToMyInventory(item),
            buyInventory: (item) => this.buyInventory(item),
            CardJitsuDownloadedAnimation: (animation) => this.CardJitsuDownloadedAnimation(animation),
            closeRoom: () => this.closeRoom(),
            closeWidget: () => this.closeWidget(),
            getCurrentServerRoomId: () => this.getCurrentServerRoomId(),
            getFrameColor: () => this.getFrameColor(),
            getGamesPath: () => this.getGamesPath(),
            getLocalizedFrame: () => this.getLocalizedFrame(),
            getMyLastRoom: () => this.getMyLastRoom(),
            getMyPlayer: () => this.getMyPlayerData(),
            getMyPlayerData: () => this.getMyPlayerData(),
            getMyPlayerHex: () => this.getMyPlayerHex(),
            getMyPlayerId: () => this.getMyPlayerId(),
            getMyPlayerNickname: () => this.getMyPlayerNickname(),
            getPath: () => this.getPath(),
            getPlayerObjectById: (id) => this.getPlayerObjectById(id),
            getPuffleColor: () => this.getPuffleColor(),
            isItemInMyInventory: (item) => this.isItemInMyInventory(item),
            isItemOnMyPlayer: (item) => this.isItemOnMyPlayer(item),
            isMyPlayerMember: () => this.isMyPlayerMember(),
            joinRoom: (roomId) => this.joinRoom(roomId),
            loadCardJitsuWorld: () => this.loadCardJitsuWorld(),
            onLoadComplete: () => this.onLoadComplete(),
            onWidgetLoadComplete: () => this.onWidgetLoadComplete(),
            playerWearingItem: (item) => this.playerWearingItem(item),
            sendDancingPacket: (payload) => this.sendDancingPacket(payload),
            sendEndMidwayGame: (payload) => this.sendEndMidwayGame(payload),
            sendGameEvent: (payload) => this.sendGameEvent(payload),
            sendGameOver: (payload) => this.sendGameOver(payload),
            sendGameOverAltLoader: (payload) => this.sendGameOverAltLoader(payload),
            sendJoinLastRoom: () => this.sendJoinLastRoom(),
            stampEarned: (id) => this.handleStampEarned(id, false),
            stampEarnedAltLoader: (id) => this.handleStampEarned(id, true),
            stampIsOwnedByMe: (id) => this.stampIsOwnedByMe(id),
            startGameMusic: (customMusic) => this.startGameMusic(customMusic),
            startMusicById: (ids) => this.startMusicById(ids),
            stopGameMusic: () => this.stopGameMusicBridge(),
            stopMusic: () => this.stopMusicBridge()
        }
    }

    createSecureFlashInterface() {
        const exposedMethods = new Set([
            'getKeys',
            ...this.bridgeKeys,
            ...this.privateBridgeKeys
        ])

        const proxy = {}

        exposedMethods.forEach(method => {
            if (typeof window.FlashBridgeHelper?.[method] === 'function') {
                proxy[method] = (...args) => window.FlashBridgeHelper[method](...args)
            }
        })

        window.ruffle = proxy
    }

    destroySecureFlashInterface() {
        if (window.ruffle) {
            delete window.ruffle
        }
    }

    normalizeGameConfig(game, musicOverride) {
        if (!game) {
            return null
        }

        if (typeof game === 'string') {
            const trimmed = game.trim()
            const path = this.normalizeGamePath(trimmed)
            const segments = trimmed.split('/')

            return {
                path,
                music: musicOverride ?? 0,
                preLoop: null,
                mainLoops: null,
                gameName: segments[0] || null,
                isQuest: false,
                useAltLoader: false
            }
        }

        if (typeof game === 'object') {
            const path = game.path
            const normalizedPath = ABSOLUTE_URL_REGEX.test(path)
                ? path
                : this.normalizeGamePath(path)

            const mainLoops = Array.isArray(game.mainLoops)
                ? [...game.mainLoops]
                : game.mainLoops
                    ? [game.mainLoops]
                    : null

            return {
                path: normalizedPath,
                music: musicOverride ?? (game.music ?? 0),
                preLoop: game.preLoop ?? null,
                mainLoops,
                gameName: game.key ?? game.gameName ?? null,
                isQuest: !!game.quest,
                useAltLoader: !!(game.use_alt_loader ?? game.useAltLoader)
            }
        }

        return null
    }

    normalizeGamePath(path) {
        if (!path) {
            return ''
        }

        if (ABSOLUTE_URL_REGEX.test(path)) {
            return path
        }

        const trimmed = path.replace(/^\/+/g, '')

        if (trimmed.startsWith(GAMES_PATH_PREFIX)) {
            return trimmed
        }

        return `${GAMES_PATH_PREFIX}${trimmed}`
    }

    resolveUrl(relativePath) {
        if (!relativePath) {
            return this.basePath
        }

        if (ABSOLUTE_URL_REGEX.test(relativePath)) {
            return relativePath
        }

        const trimmed = relativePath.replace(/^\/+/g, '')
        return `${this.basePath}${trimmed}`
    }

    getFrameColor() {
        return this.crumbs?.frameColor ?? '#000000'
    }

    getPath() {
        return this.resolveUrl(this.path)
    }

    getGamesPath() {
        return this.resolveUrl(GAMES_PATH_PREFIX)
    }

    getMyPlayerData() {
        const client = this.client

        if (!client) {
            return null
        }

        const items = this.clientObject

        return {
            color: items.color ?? 1,
            head: items.head ?? 0,
            face: items.face ?? 0,
            neck: items.neck ?? 0,
            body: items.body ?? 0,
            hand: items.hand ?? 0,
            feet: items.feet ?? 0,
            flag: items.flag ?? 0,
            photo: items.photo ?? 0
        }
    }

    getMyPlayerHex() {
        const colorId = this.clientObject?.color

        if (!colorId) {
            return '#000000'
        }

        return this.world?.getColor(colorId) ?? '#000000'
    }

    getMyPlayerId() {
        return this.client?.id ?? 0
    }

    getMyPlayerNickname() {
        return this.client?.penguin?.displayName ?? ''
    }

    getMyLastRoom() {
        return this.world?.lastRoom ?? 100
    }

    getCurrentServerRoomId() {
        return this.world?.room?.id ?? 0
    }

    getLocalizedFrame() {
        return 1
    }

    getPlayerObjectById(id) {
        const myId = this.getMyPlayerId()

        if (Array.isArray(id)) {
            id = id[0]
        }

        if (id !== myId) {
            return null
        }

        return this.getMyPlayerData()
    }

    getPuffleColor() {
        return this.client?.penguin?.puffle?.type ?? 0
    }

    isItemOnMyPlayer(item) {
        const itemId = sanitizeItemId(item)

        if (!itemId) {
            return false
        }

        const equipped = this.client?.penguin?.items?.equippedFlat ?? {}

        return Object.values(equipped).includes(itemId)
    }

    isItemInMyInventory(item) {
        const itemId = sanitizeItemId(item)

        if (!itemId) {
            return false
        }

        const inventory = this.client?.inventory

        if (!inventory) {
            return false
        }

        if (Array.isArray(inventory)) {
            return inventory.includes(itemId)
        }

        return Object.values(inventory).some(slot => Array.isArray(slot) ? slot.includes(itemId) : slot === itemId)
    }

    playerWearingItem(item) {
        return this.isItemOnMyPlayer(item)
    }

    isMyPlayerMember() {
        return true
    }

    addEventListener(...args) {
        this.eventListeners.push([...args])
        this.gameEvents.addEventListener(...args)
    }

    removeEventListeners() {
        this.eventListeners.forEach(args => this.gameEvents.removeEventListener(...args))
        this.eventListeners = []
    }

    onLoadComplete() {
        this.showGameSurface({ hideInterface: true, stopMusic: true })
    }

    onWidgetLoadComplete() {
        this.showGameSurface({ hideInterface: false, hideLoading: false, stopMusic: false })

        this.interface?.main?.events?.emit?.('hide_main_inputs')
    }

    sendGameEvent(obj) {
        if (!obj || typeof obj.event !== 'string') {
            return false
        }

        this.gameEvents.dispatchEvent(new CustomEvent(obj.event, {
            detail: obj.args
        }))

        return true
    }

    sendGameOver(payload) {
        if (this.isQuest) {
            this.close()
            return true
        }

        let coins = 0

        if (typeof payload === 'number') {
            coins = payload
        } else if (payload && typeof payload === 'object') {
            if (payload.tickets !== undefined) {
                coins = payload.tickets
            } else {
                coins = payload.coins
            }
        }

        coins = clampCoins(coins)

        const gameOverPayload = {
            coins,
            game: this.gameName ?? 'unknown'
        }

        // §5 Anti-Cheat: Attach the session token (if available)
        if (this.gameSessionToken) {
            gameOverPayload.token = this.gameSessionToken
            this.gameSessionToken = null // One-time use
        }

        this.network?.send('game_over', gameOverPayload)

        this.close()
        return true
    }

    sendGameOverAltLoader(payload) {
        if (!Array.isArray(payload) || payload.length === 0) {
            return false
        }

        const coins = clampCoins(payload[0])

        const gameOverPayload = { coins }

        // §5 Anti-Cheat: Attach the session token (if available)
        if (this.gameSessionToken) {
            gameOverPayload.token = this.gameSessionToken
            gameOverPayload.game = this.gameName ?? 'unknown'
            this.gameSessionToken = null
        }

        this.network?.send('game_over', gameOverPayload)
        this.close()
        return true
    }

    sendEndMidwayGame(payload) {
        return this.sendGameOver(payload ?? { coins: 0 })
    }

    sendJoinLastRoom() {
        this.close()
        this.client?.sendJoinLastRoom()
        return true
    }

    joinRoom(roomId) {
        const id = parseInt(Array.isArray(roomId) ? roomId[0] : roomId, 10)

        if (!Number.isFinite(id)) {
            return false
        }

        const room = this.crumbs?.rooms?.[id]

        if (!room) {
            console.warn('[RuffleController] Invalid room id from Ruffle bridge', roomId)
            return false
        }

        this.close()
        this.client?.sendJoinRoom(id, room.key, room.x, room.y, 80)
        return true
    }

    loadCardJitsuWorld() {
        if (this.world?.room?.key === 'Card') {
            this.world.room.loadCardJitsu?.()
        }
    }

    CardJitsuDownloadedAnimation(animation) {
        if (!Array.isArray(animation) || !this.world?.room?.loadAnimation) {
            return
        }

        this.world.room.loadAnimation(animation[0])
    }

    sendDancingPacket() {
        return false
    }

    async addItemToMyInventory(item) {
        const itemId = sanitizeItemId(item)

        if (!itemId || this.isItemInMyInventory(itemId)) {
            return false
        }

        // Use WebAPI for item purchases (CPI architecture pattern)
        if (this.network?.webapi?.isAuthenticated()) {
            try {
                const result = await this.network.webapi.purchaseItem(itemId)
                if (result.success !== false && !result.error) {
                    // Update local inventory
                    const data = this.crumbs?.items?.[itemId]
                    const slot = data?.slot || 'body'
                    if (this.world?.client?.inventory?.[slot]) {
                        if (!this.world.client.inventory[slot].includes(itemId)) {
                            this.world.client.inventory[slot].push(itemId)
                            this.world.client.inventory[slot].sort((a, b) => a - b)
                        }
                    }
                    if (result.coins !== undefined) {
                        this.world.client.coins = result.coins
                    }
                    return true
                }
            } catch (error) {
                console.warn('WebAPI item purchase failed, falling back to socket:', error)
            }
        }

        // Fallback to Socket.IO
        this.network?.send('add_item', { item: itemId })
        return true
    }

    buyInventory(item) {
        const itemId = sanitizeItemId(item)

        if (!itemId) {
            return false
        }

        this.interface?.prompt?.showItem?.(itemId)
        return true
    }

    startMusicById(ids) {
        const key = normalizeMusicKey(ids)

        if (!key) {
            return false
        }

        return this.startGameMusic(key)
    }

    startGameMusic(customMusic = false) {
        const explicitKey = normalizeMusicKey(customMusic)
        const defaultKey = normalizeMusicKey(this.music)
        const hasLoopConfig = normalizeMusicKey(this.preLoop) !== null || normalizeMusicList(this.mainLoops).length > 0

        if (explicitKey) {
            return this.startPlayingMusic(explicitKey)
        }

        if (hasLoopConfig) {
            return this.startPlayingLoopedMusic(this.preLoop, this.mainLoops)
        }

        if (defaultKey) {
            return this.startPlayingMusic(defaultKey)
        }

        return false
    }

    startPlayingMusic(music) {
        const key = normalizeMusicKey(music)

        if (!key) {
            return false
        }

        if (this.cache?.audio?.exists(key)) {
            this.playMusic(key)
            return true
        }

        this.load?.audio(key, `assets/media/music/${key}.mp3`)
        this.load?.start()
        this.load?.once(`filecomplete-audio-${key}`, () => this.playMusic(key))

        return true
    }

    startPlayingLoopedMusic(preLoop, mainLoops) {
        const preLoopKey = normalizeMusicKey(preLoop)
        const loopKeys = normalizeMusicList(mainLoops)
        const allLoops = [preLoopKey, ...loopKeys].filter(Boolean)

        if (allLoops.length === 0) {
            return false
        }

        if (typeof this.soundManager?.playLoopedMusic === 'function') {
            this.soundManager.playLoopedMusic(preLoopKey, loopKeys.length > 0 ? loopKeys : null)
            return true
        }

        return this.startPlayingMusic(allLoops[0])
    }

    stopGameMusicBridge() {
        try {
            this.stopMusic()
        } catch (error) {
            console.warn('[RuffleController] Failed to stop tracked music layers', error)
        }

        try {
            this.soundManager?.stopAll()
        } catch (error) {
            console.warn('[RuffleController] Failed to stop Howler audio while stopping game music', error)
        }

        this.forceStopRuffleAudio()
        return true
    }

    stopMusicBridge() {
        return this.stopGameMusicBridge()
    }

    stampIsOwnedByMe(id) {
        const stampId = sanitizeItemId(id)

        if (!stampId) {
            return false
        }

        const stamps = this.client?.stamps ?? []
        return stamps.includes(stampId)
    }

    handleStampEarned(id, fromAltLoader) {
        const stampId = sanitizeItemId(id)

        if (!stampId || this.stampIsOwnedByMe(stampId)) {
            return false
        }

        try {
            this.network?.events?.emit?.('stamp_earned', { stamp: stampId, source: 'ruffle' })
        } catch (error) {
            console.warn('[RuffleController] Failed to emit local stamp_earned event', error)
        }

        this.stampEarned(stampId, fromAltLoader)

        if (fromAltLoader && this.player?.onGetStamp) {
            const stampObj = this.world?.stampController?.getStamp?.(stampId)

            if (stampObj) {
                this.player.onGetStamp([stampObj.stamp_id, stampObj.name, stampObj.rank])
            }
        }

        return true
    }

    onBroadcast(message) {
        this.player?.onBroadcast?.(message)
    }

    playCJAnimation(seat, animation) {
        this.player?.playAnimation?.({ winseat: seat, name: animation })
    }

    closeWidget() {
        try {
            if (this.player?.pause) {
                this.time.delayedCall(100, () => this.player.pause())
            }

            if (this.container) {
                this.container.visible = false
            }

            this.setPointerEnabled(false)

            this.interface?.main?.events?.emit?.('show_main_inputs')
        } finally {
            this.removeEventListeners()
        }
    }

    closeRoom() {
        try {
            if (this.player?.pause) {
                this.time.delayedCall(100, () => this.player.pause())
            }

            if (this.container) {
                this.container.visible = false
            }

            this.setPointerEnabled(false)

            if (this.game?.domContainer) {
                this.game.domContainer.style.zIndex = 'auto'
            }
        } finally {
            this.removeEventListeners()
        }
    }

    close() {
        this.closeWidget()
        this.interface?.showInterface?.()
        this.stop()
    }

    resetDepth() {
        if (this.game?.domContainer) {
            this.game.domContainer.style.zIndex = 'auto'
        }
    }

    sendToBack() {
        if (this.game?.domContainer) {
            this.game.domContainer.style.zIndex = -10
        }
    }

    showGameSurface({ hideInterface = true, hideLoading = true, stopMusic = true } = {}) {
        if (stopMusic) {
            try {
                this.stopMusic()
            } catch (error) {
                console.warn('[RuffleController] Failed to stop music while showing game surface', error)
            }
        }

        if (hideLoading) {
            this.interface?.hideLoading?.()
        }

        if (hideInterface) {
            this.interface?.hideInterface?.()
        }

        if (this.container) {
            this.container.visible = true
        }

        this.setPointerEnabled(true)
        this.resetDepth()

        this.gameSurfaceVisible = true
    }

    applyPointerState(node) {
        if (!node || !node.style) {
            return
        }

        node.style.pointerEvents = this.domPointerEnabled ? 'auto' : 'none'
    }

    setPointerEnabled(enabled) {
        this.domPointerEnabled = !!enabled

        this.applyPointerState(this.container?.node)
        this.applyPointerState(this.player)

        if (this.domContainer && this.domContainer.style) {
            this.domContainer.style.pointerEvents = this.domPointerEnabled ? 'auto' : 'none'
        }
    }

}
